/**
 * Presence hooks: one writer (the heartbeat) and one reader (the poll).
 *
 * The heartbeat beats only while the tab is visible, so a window left open
 * overnight doesn't keep you green — and beats immediately when you come back,
 * so returning to the tab doesn't leave you offline for up to 30 seconds.
 *
 * In the desktop app the page always reads "visible" (it keeps running when
 * hidden in the tray), so there the window's real state decides.
 */
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchOnlineUserIds, sendHeartbeat } from '../lib/presenceApi';
import { currentDesktopWindowState, isDesktop } from '../lib/desktop';
import { useDesktopWindowState } from './useDesktopWindowState';
import { useAuthStore } from '../store/useAuthStore';

/** Mirrors HEARTBEAT_MS in backend/src/routes/presence.ts. */
const HEARTBEAT_MS = 30_000;

/**
 * Keep the caller marked online. Mount EXACTLY ONCE, app-wide (Layout) — a
 * second instance would just double the write traffic for no benefit.
 */
export function useHeartbeat() {
  const isAuthenticated = useAuthStore((s) => s.accessToken !== null);
  // Re-runs (and beats at once) when the desktop window is shown again.
  const desktopVisible = useDesktopWindowState().visible;

  useEffect(() => {
    if (!isAuthenticated) return;

    const beat = () => {
      const visible = isDesktop()
        ? currentDesktopWindowState().visible
        : document.visibilityState === 'visible';
      if (!visible) return;
      void sendHeartbeat().catch(() => undefined);
    };

    beat();
    const timer = window.setInterval(beat, HEARTBEAT_MS);
    document.addEventListener('visibilitychange', beat);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', beat);
    };
  }, [isAuthenticated, desktopVisible]);
}

/**
 * The set of user ids currently online. Polled slightly faster than the
 * heartbeat so a fresh beat shows up within one cycle.
 */
export function useOnlineUserIds(): ReadonlySet<string> {
  const query = useQuery({
    queryKey: ['presence', 'online'],
    queryFn: fetchOnlineUserIds,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 25_000,
    // Module-level `select` so react-query memoizes the Set: it is rebuilt only
    // when a poll actually returns new data, not on every render.
    select: toSet,
  });
  return query.data ?? EMPTY;
}

const EMPTY: ReadonlySet<string> = new Set<string>();

function toSet(ids: string[]): ReadonlySet<string> {
  return new Set(ids);
}
